// Dependencies
// ------------

import './polyfills/closest';

import addEventListeners from './events/addEventListeners';
import getLeadformVersion from '../leadform/models/metadata/get_leadform_version';
import Page from 'llt-action-tracker/dist/events/page';
import getVisitorId from '../leadform/models/metadata/get_visitor_id';

// Internal
// --------

const PANEL_EVENTS = [
  'mslf.account-panel.complete',
  'mslf.account-email-panel.complete',
  'mslf.applying-to.complete',
  'mslf.attempted-usmle-steps.complete',
  'mslf.camp-grade-level.complete',
  'mslf.cert-test.complete',
  'mslf.company-panel.complete',
  'mslf.completed-pmi-approved-contact-hours.complete',
  'mslf.contact-preference.complete',
  'mslf.current-country.complete',
  'mslf.current-position.complete',
  'mslf.currently-in-project-management-industry.complete',
  'mslf.currently-preparing-for-other-series-licenses.complete',
  'mslf.email-panel.complete',
  'mslf.grade-level.complete',
  'mslf.has-learning-difference.complete',
  'mslf.have-authorization-to-test.complete',
  'mslf.have-other-series-licenses.complete',
  'mslf.have-you-taken.complete',
  'mslf.how-often.complete',
  'mslf.how-soon-admissions-consulting.complete',
  'mslf.how-soon-they.complete',
  'mslf.how-soon-you.complete',
  'mslf.how-soon.complete',
  'mslf.learning-goal.complete',
  'mslf.licensed-medical-doctor-outside-usa.complete',
  'mslf.name-panel.complete',
  'mslf.parent-student.complete',
  'mslf.partnerships-type.complete',
  'mslf.partnerships-type-school.complete',
  'mslf.passed-before-someone-else.complete',
  'mslf.passed-before.complete',
  'mslf.phone-panel.complete',
  'mslf.pid-panel.complete',
  'mslf.preparing-for-which-other-series-licenses.complete',
  'mslf.preparing-how.complete',
  'mslf.product-type.complete',
  'mslf.discount-code-panel.complete',
  'mslf.redirecturl.complete',
  'mslf.product-type-redirect.complete',
  'mslf.product-type-school.complete',
  'mslf.student-grade-elementary.complete',
  'mslf.student-grade-middle.complete',
  'mslf.student-grade-other.complete',
  'mslf.subject-panel.complete',
  'mslf.taken-before.complete',
  'mslf.taking-when.complete',
  'mslf.test-prep-or-admissions-consulting.complete',
  'mslf.text-panel.complete',
  'mslf.thank-you-form.complete',
  'mslf.thank-you-panel-link.complete',
  'mslf.thank-you-panel.complete',
  'mslf.tutoring-for.complete',
  'mslf.tutoring-type.complete',
  'mslf.vashon-panel-1.complete',
  'mslf.vashon-panel-2.complete',
  'mslf.vashon-panel-3.complete',
  'mslf.welcome-alternate.complete',
  'mslf.welcome.complete',
  'mslf.what-are-they-applying-to.complete',
  'mslf.what-are-you-applying-to.complete',
  'mslf.what-camp-topics.complete',
  'mslf.what-timeframe.complete',
  'mslf.which-component.complete',
  'mslf.which-exam.complete',
  'mslf.which-level.complete',
  'mslf.which-subject.complete',
  'mslf.which-test-are-they-preparing-for.complete',
  'mslf.which-test-are-you-preparing-for.complete',
  'mslf.which-test.complete',
  'mslf.who-is-the-student.complete',
  'mslf.who-needs-admissions-consulting.complete',
  'mslf.who-needs-test-prep.complete',
  'mslf.who-needs-tutor.complete',
  'mslf.zip-panel.complete'
];

export function trackingConfig() {
  const defaults = {
    action_tracker: {
      endpoint: `https://${process.env.eventsHost}/v2/pages/interaction`,
      application_id: 'components'
    }
  };
  const params = window.components || defaults;

  return {
    endpoint: params.action_tracker.endpoint,
    applicationId: params.action_tracker.application_id
  };
}

/**
 * Sends event data to New Relic
 *
 * @param {string} selector - Selector being tracked
 * @param {string} action - Action name
 * @param {object} target - Event target
 * @returns {boolean} Always returns `true` to ensure the native click handler fires
 */
export function track(selector, action, target) {
  const form = target.closest('.leadform');
  const version = getLeadformVersion(form);

  sendToActionTracker(action, version);
  sendToNewRelic(selector, action, version);

  return true;
}

/**
 * Send event to New Relic
 *
 * @param   {string}  selector         The target element
 * @param   {string}  leadFormVersion  The lead form version
 *
 */
function sendToNewRelic(selector, action, leadFormVersion) {
  const data = {
    target: selector,
    visitorId: getVisitorId(),
    leadformVersion: leadFormVersion
  };

  window.newrelic && window.newrelic.addPageAction(action, data);
}

/**
 * Send event to action tracker
 *
 * @param   {string}  action           Lead form action
 * @param   {string}  leadFormVersion  Lead form version
 *
 * @return  {[type]}                   [return description]
 */
export function sendToActionTracker(action, leadFormVersion, args = {}) {
  const label = action.replace(/\./g, '_');
  const data = Object.assign({
    target: label,
    dataAttributes: {
      leadformVersion: leadFormVersion,
      leadformAction: action
    },
    category: 'leadform-panel',
    label: label
  }, args);

  const config = trackingConfig();
  const page = Page({endpoint: config.endpoint, applicationId: config.applicationId});
  return page.track('lead-form', data);
}

/**
 * Sets up event tracking on submit buttons and panel events
 */
function setUpEventListeners() {
  // Track submit button clicks
  addEventListeners('.tr-lead-constructor-submit', 'click', function (event) {
    track('.tr-lead-constructor-submit', 'form-submit', event.target);
  });

  // Track each wizard panel as it is completed
  addEventListeners('.js-leadform', PANEL_EVENTS, function (event) {
    track('.tr-mslf-panel', event.type, event.target);
  });
}

// Exports
// -------

/**
 * Sets up the event tracking of form events through New Relic
 */
export function trackEvents() {
  setUpEventListeners();
}
